import BaseService from './baseService';
import roleTypes from '../constants/roleTypes';
import trimStart from 'lodash/trimStart';

class ArtistService {
    static getFeaturedArtists () {    
        return BaseService.getAndReturn('/FeaturedArtists', BaseService.returnAssignedArray, 'Unable to fetch featured artists', false);
    } 

    static getProfile(username, currentProfileID) {
        return BaseService.getAndReturn(`/Profiles/${username}?currentProfileID=${currentProfileID}`, BaseService.returnAssignedObject, 'Unable to fetch profile', true);
    }

    static getBio(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/Bio`, BaseService.returnAssignedArray, 'Unable to fetch bio', true);
    }

    static getMusic(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/Music`, BaseService.returnAssignedArray, 'Unable to fetch music', true);
    }

    static getVideos(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/Videos`, BaseService.returnAssignedArray, 'Unable to fetch videos', true);
    }

    static getGigs(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/Gigs`, BaseService.returnAssignedArray, 'Unable to fetch gigs', true);
    }

    static getBuyOptions(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/BuyOptions`, BaseService.returnAssignedObject, 'Unable to fetch buy options', true);
    }

    static getAddress(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/Address`, BaseService.returnAssignedObject, 'Unable to fetch address', true);
    }

    static getComments(username) {
        return BaseService.getAndReturn(`/Profiles/${username}/Comments`, BaseService.returnAssignedArray, 'Unable to fetch comments', true);
    }

    static getConversation(username, counterUsername) {
        return BaseService.getAndReturn(`/Profiles/${username}/Conversation/${counterUsername}`, BaseService.returnAssignedObject, 'Unable to fetch conversation', true);
    }

    static addComment(username, fromRoleID, text) {
        return BaseService.postAndReturn(`/Profiles/${username}/Comments`, { fromRoleID, text }, BaseService.returnAssignedObject, 'Unable to add comment', true)
    }

    static approveComment(username, commentID) {
        return BaseService.postAndReturn(`/Profiles/${username}/Comments/${commentID}/Approve`, { }, BaseService.returnAssignedObject, 'Unable to approve comment', true)
    }

    static deleteComment(username, commentID) {
        return BaseService.deleteAndReturn(`/Profiles/${username}/Comments/${commentID}`, (data) => data, 'Unable to delete comment', true);
    }

    static getConnections(username, inbound) {
        return BaseService.getAndReturn(`/Profiles/${username}/Connections/${inbound ? 'inbound':'outbound'}`, BaseService.returnAssignedArray, 'Unable to fetch connections', true);
    }

    static addConnection(username, connectToProfileID) {
        return BaseService.postAndReturn(`/Profiles/${username}/Connections`, connectToProfileID, (data) => data, 'Unable to create connection', true);
    }

    static deleteConnection(username, connectToProfileID) {
        return BaseService.deleteAndReturn(`/Profiles/${username}/Connections/${connectToProfileID}`, (data) => data, 'Unable to delete connection', true);
    }

    static getAssociations(query, roleType) {
        return BaseService.getAndReturn(`/Associations?query=${query}&roleType=${roleType}`, BaseService.returnAssignedArray, 'Unable to fetch influences', true, 'Unable to get influences');
    }

    static browseProfiles (roleTypeRoute, keyword, genres, associationIDs, distance, distanceUnit, latitude, longitude, instrument, expertise, capacityMin, capacityMax, sort, page) {    
        let url = '';
        if(keyword) {
            url = `/${roleTypeRoute}?page=${page}&keyword=${keyword}`;
        }
        else {
            let genreQuery = '';
            genres.forEach(genre => {
                genreQuery += `&genres=${encodeURIComponent(genre)}`;
            });
            let associationQuery = '';
            associationIDs.forEach(associationID => {
                associationQuery += `&associationIDs=${associationID}`;
            });    
            url = `/${roleTypeRoute}?page=${page}${genreQuery}${associationQuery}&distance=${distance || ''}&distanceUnit=${distanceUnit || ''}&latitude=${latitude || ''}&longitude=${longitude || ''}&instrument=${instrument || ''}&expertise=${expertise || ''}&capacityMin=${capacityMin || ''}&capacityMax=${capacityMax || ''}&sort=${sort || ''}`;
        }        
        return BaseService.getAndReturn(url, BaseService.returnAssignedObject, 'Unable to complete search', true, 'Unable to complete search');
    } 

    static getRoleTypeFriendlyName(roleType) {
        return roleTypes.find(r => r.type == roleType).friendlyName;
    }

    static showGenre(roleType) {
        return roleType !== 'Venue' && roleType !== 'RecordingStudio';
    }

    static showMusic(roleType)
    {
        return roleType === 'Artist' || roleType === 'Fan' || roleType === 'Musician' || roleType === 'Producer' || roleType === 'Songwriter';
    }

    static showVideos(roleType)
    {
        return roleType === 'Artist' || roleType === 'Fan' || roleType === 'Musician';
    }
       
    static showGigs(roleType)
    {
        return roleType === 'Artist' || roleType === 'Musician';
    }
      
    static showBuyLinks(roleType)
    {
        return roleType === 'Artist' || roleType === 'Musician' || roleType === 'Producer';
    }  
        
    static showFindMe(roleType)
    {
        return roleType === 'Venue' || roleType === 'RecordingStudio';
    }  

    static showInstrument(roleType) {
        return roleType === 'Musician';
    }

    static showCapacity(roleType) {
        return roleType === 'Venue';
    }

    static showRating(roleType) {
        return roleType === 'Artist' || roleType === 'Musician' || roleType === 'Producer';      
    }

    static showInfluences(roleType) {
        return roleType === 'Artist' || roleType === 'Producer' || roleType === 'Fan' || roleType === 'Musician' || roleType === 'Songwriter';
    }

    static getTwitterUsername(artist){        
        return artist.TwitterUsername ? trimStart(artist.TwitterUsername, '@') : '';
    }

    static getRoleFilterOptions(roleType) {
        let filters = [];
        if(this.showInstrument(roleType)) {
            filters.push('instrument');
        }
        else if(this.showCapacity(roleType)) {
            filters.push('capacity');
        }
        else if(this.showGenre(roleType)) {
            filters.push('genre');
        }
        else if(this.showInfluences(roleType)) {
            filters.push(roleType == 'Fan' ? 'favourite artists' : 'influences');
        }
        let filter = 'location';
        for(let i = 0; i < filters.length; i++) {
            if(i < filters.length - 1) {
                filter += ', ' + filters[i];
            }
            else {
                filter += ' and ' + filters[i];
            }
        }
        return filter;
    }

    static getDefaultImage(roleType) {
        if(roleType == 'Fan') {
            return require('../images/profile-fan.png');
        }
        else if(roleType == 'Artist') {
            return require('../images/profile-artist.png');
        }
        else if(roleType == 'Producer') {
            return require('../images/profile-producer.png');
        }
        else if(roleType == 'Musician') {
            return require('../images/profile-musician.png');
        }
        else if(roleType == 'Manager') {
            return require('../images/profile-manager.png');
        }
        else if(roleType == 'Promoter') {
            return require('../images/profile-promoter.png');
        }
        else if(roleType == 'RecordingStudio') {
            return require('../images/profile-recordingstudio.png');
        }
        else if(roleType == 'RecordLabel') {
            return require('../images/profile-recordlabel.png');
        }
        else if(roleType == 'Songwriter') {
            return require('../images/profile-writer.png');
        }
        else if(roleType == 'Venue') {
            return require('../images/profile-venue.png');
        }
        else if(roleType == 'Writer') {
            return require('../images/profile-writer.png');
        }
    }
}
export default ArtistService;